import { gsap } from 'gsap';

/**
 * Module AccountFormManager
 * Gère les onglets, les animations et la validation des formulaires
 * sur la page "Mon Compte".
 */
class AccountFormManager {
    constructor() {
        this.switcher = document.getElementById('form-switcher');
        if (!this.switcher) return;

        // Éléments des onglets
        this.indicator = this.switcher.querySelector('.tab-indicator');
        this.buttons = this.switcher.querySelectorAll('.tab-button');
        this.loginForm = document.getElementById('login-form');
        this.registerForm = document.getElementById('register-form');
        this.formsContainer = document.getElementById('forms-container');

        // Éléments de validation
        this.regForm = this.registerForm.querySelector('form');
        this.usernameInput = document.getElementById('reg_username');
        this.usernameValidationMessage = document.getElementById('username-validation-message');
        this.emailInput = document.getElementById('reg_email');
        this.validationMessage = document.getElementById('email-validation-message');
        this.submitButton = this.regForm ? this.regForm.querySelector('button[type="submit"]') : null;
        
        this.emailDebounceTimer = null;
        this.usernameDebounceTimer = null;
        this.isAnimating = false;

        this.init();
    }

    init() {
        if (!this.loginForm || !this.registerForm || !this.formsContainer || !this.regForm || !this.usernameInput || !this.emailInput) {
            console.error('AccountFormManager: Un ou plusieurs éléments DOM requis sont manquants.');
            return;
        }
        this.setupInitialLayout();
        this.addEventListeners();
        this.handleRegistrationSuccess();
    }

    setupInitialLayout() {
        gsap.set(this.formsContainer, { position: 'relative', height: this.loginForm.offsetHeight });
        gsap.set(this.loginForm, { opacity: 1, position: 'absolute', top: 0, left: 0, width: '100%', visibility: 'visible' });
        gsap.set(this.registerForm, { opacity: 0, position: 'absolute', top: 0, left: 0, width: '100%', visibility: 'hidden' });
    }

    addEventListeners() {
        this.buttons.forEach(button => {
            button.addEventListener('click', this.handleTabClick.bind(this));
        });

        this.usernameInput.addEventListener('input', this.handleUsernameInput.bind(this));
        this.emailInput.addEventListener('input', this.handleEmailInput.bind(this));
        this.regForm.addEventListener('submit', this.handleRegFormSubmit.bind(this));
        
        window.addEventListener('resize', () => this.updateContainerHeight());
    }

    handleTabClick(e) {
        const activeButton = e.currentTarget;
        if (this.isAnimating || activeButton.getAttribute('aria-selected') === 'true') {
            return;
        }
        const targetTab = activeButton.dataset.tab;
        this.switchTab(targetTab, activeButton);
    }

    switchTab(targetTab, activeButton) {
        this.isAnimating = true;

        this.buttons.forEach(btn => {
            btn.setAttribute('aria-selected', 'false');
            btn.classList.remove('text-white', 'hover:text-gray-900');
            btn.classList.add('text-gray-900', 'hover:text-orange-500');
        });
        activeButton.setAttribute('aria-selected', 'true');
        activeButton.classList.remove('text-gray-900', 'hover:text-orange-500');
        activeButton.classList.add('text-white', 'hover:text-gray-900');

        const buttonIndex = Array.from(this.buttons).indexOf(activeButton);
        gsap.to(this.indicator, {
            left: `${buttonIndex * 50}%`,
            duration: 0.4,
            ease: 'power2.inOut'
        });

        const formToShow = targetTab === 'login' ? this.loginForm : this.registerForm;
        const formToHide = targetTab === 'login' ? this.registerForm : this.loginForm;
        
        const newHeight = formToShow.offsetHeight;
        const tl = gsap.timeline({ onComplete: () => { this.isAnimating = false; } });

        tl.to(this.formsContainer, { height: newHeight, duration: 0.4, ease: 'power2.inOut' })
          .to(formToHide, { opacity: 0, duration: 0.3, onComplete: () => gsap.set(formToHide, { visibility: 'hidden' }) }, 0)
          .set(formToShow, { visibility: 'visible' }, 0)
          .to(formToShow, { opacity: 1, duration: 0.3 }, ">-0.1");
    }
    
    updateContainerHeight() {
        const activeForm = this.loginForm.style.visibility === 'visible' ? this.loginForm : this.registerForm;
        if (activeForm) {
            gsap.set(this.formsContainer, { height: activeForm.offsetHeight });
        }
    }

    handleUsernameInput(e) {
        clearTimeout(this.usernameDebounceTimer);
        this.usernameValidationMessage.textContent = '';
        this.usernameValidationMessage.className = 'text-sm mt-1';
        
        const username = e.target.value;
        if (username.length === 0) {
            this.submitButton.disabled = true;
            return;
        }

        this.usernameDebounceTimer = setTimeout(() => this.validateUsername(username), 300);
    }

    handleEmailInput(e) {
        clearTimeout(this.emailDebounceTimer);
        this.validationMessage.textContent = '';
        this.validationMessage.className = 'text-sm mt-1';
        if (e.target.value.length < 4) return;
        this.emailDebounceTimer = setTimeout(() => this.validateEmail(e.target.value), 500);
    }

    handleRegFormSubmit(e) {
        const isUsernameValid = this.isValidUsername(this.usernameInput.value);
        const isEmailValid = this.isValidEmail(this.emailInput.value);

        if (!isUsernameValid || !isEmailValid || this.validationMessage.classList.contains('text-red-600') || this.usernameValidationMessage.classList.contains('text-red-600')) {
            e.preventDefault();
            if (!isUsernameValid) {
                this.validateUsername(this.usernameInput.value);
            }
            if (!isEmailValid) {
                this.validateEmail(this.emailInput.value, true);
            }
        }
    }

    isValidUsername(username) {
        // WordPress allows alphanumeric, space, underscore, hyphen, and period.
        const re = /^[a-zA-Z0-9 ._-]+$/;
        return re.test(String(username));
    }

    isValidEmail(email) {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    }

    validateUsername(username) {
        if (!this.isValidUsername(username)) {
            this.usernameValidationMessage.textContent = 'Caractères non autorisés dans l\'identifiant.';
            this.usernameValidationMessage.className = 'text-sm mt-1 text-red-600';
            this.submitButton.disabled = true;
        } else {
            this.usernameValidationMessage.textContent = '';
            this.usernameValidationMessage.className = 'text-sm mt-1';
            // Only enable submit if email is also valid
            if (this.isValidEmail(this.emailInput.value) && !this.validationMessage.classList.contains('text-red-600')) {
                 this.submitButton.disabled = false;
            }
        }
    }

    validateEmail(email) {
        if (!this.isValidEmail(email)) {
            this.validationMessage.textContent = 'Veuillez entrer une adresse e-mail valide.';
            this.validationMessage.className = 'text-sm mt-1 text-red-600';
            this.submitButton.disabled = true;
            return;
        }

        if (typeof registrationValidationData === 'undefined') {
            console.error('registrationValidationData non défini.');
            return;
        }

        const data = new FormData();
        data.append('action', 'check_email_exists');
        data.append('email', email);
        data.append('nonce', registrationValidationData.nonce);

        fetch(registrationValidationData.ajax_url, { method: 'POST', body: data })
        .then(response => response.json())
        .then(result => {
            if (result.success) {
                if (result.data.exists) {
                    this.validationMessage.textContent = 'Cette adresse e-mail est déjà utilisée.';
                    this.validationMessage.className = 'text-sm mt-1 text-red-600';
                    this.submitButton.disabled = true;
                } else {
                    this.validationMessage.textContent = 'Adresse e-mail disponible.';
                    this.validationMessage.className = 'text-sm mt-1 text-green-600';
                    this.submitButton.disabled = false;
                }
            } else {
                this.validationMessage.textContent = result.data.message || 'Une erreur est survenue.';
                this.validationMessage.className = 'text-sm mt-1 text-red-600';
                this.submitButton.disabled = true;
            }
        })
        .catch(error => {
            console.error('Erreur de validation E-mail:', error);
            this.validationMessage.textContent = 'Erreur de connexion. Veuillez réessayer.';
            this.validationMessage.className = 'text-sm mt-1 text-red-600';
            this.submitButton.disabled = true;
        });
    }

    handleRegistrationSuccess() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('registration') && urlParams.get('registration') === 'success') {
            const loginButton = this.switcher.querySelector('button[data-tab="login"]');
            if(loginButton) {
                this.switchTab('login', loginButton);
            }
        }
    }
}

export default AccountFormManager;
